import { LucideIcon as LucideIconType } from 'lucide-react';
import { Box, BoxProps } from '@mui/material';
import { styled } from '@mui/material/styles';

type LucideIconProps = Omit<BoxProps, 'color'> & {
	icon: LucideIconType;
	size?: number | string;
	color?: 'inherit' | 'disabled' | 'primary' | 'secondary' | 'action' | 'error' | 'info' | 'success' | 'warning';
	strokeWidth?: number;
};

type RootProps = BoxProps & {
	size?: number | string;
	color?: 'inherit' | 'disabled' | 'primary' | 'secondary' | 'action' | 'error' | 'info' | 'success' | 'warning';
};

/**
 * Styled wrapper for Lucide icons with MUI theme integration
 */
const Root = styled(Box)<RootProps>(({ theme, size = 20, color = 'inherit' }) => ({
	width: size,
	height: size,
	minWidth: size,
	minHeight: size,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	color: {
		primary: theme.vars?.palette.primary.main || theme.palette.primary.main,
		secondary: theme.vars?.palette.secondary.main || theme.palette.secondary.main,
		info: theme.vars?.palette.info.main || theme.palette.info.main,
		success: theme.vars?.palette.success.main || theme.palette.success.main,
		warning: theme.vars?.palette.warning.main || theme.palette.warning.main,
		action: theme.vars?.palette.action.active || theme.palette.action.active,
		error: theme.vars?.palette.error.main || theme.palette.error.main,
		disabled: theme.vars?.palette.action.disabled || theme.palette.action.disabled,
		inherit: 'currentColor',
	}[color] as string,
}));

/**
 * LucideIcon component wrapper for use in the app
 * Integrates Lucide React icons with MUI theming
 *
 * @example
 * ```tsx
 * import { Layers } from 'lucide-react';
 * <LucideIcon icon={Layers} size={24} color="primary" />
 * ```
 */
export default function LucideIcon(props: LucideIconProps) {
	const { icon: Icon, size = 20, color = 'inherit', strokeWidth = 2, className, sx, ...rest } = props;

	return (
		<Root
			size={size}
			color={color}
			className={className}
			sx={{
				...sx,
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
			{...rest}
		>
			<Icon
				size={typeof size === 'number' ? size : parseInt(size.toString())}
				strokeWidth={strokeWidth}
			/>
		</Root>
	);
}
